import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useClub } from '../hooks/useClubs';
import { useClubMessages, useSendMessage } from '../hooks/useMessages';
import { type Message as ApiMessage, type SendMessagePayload } from '../lib/api/messages';
import { TopBar, ChatArea, ChatInputBar } from '../components';
import { createClubChatMobileMenu } from '../components/MobileContextMenu';
import { type Message } from '../components/ChatMessage';

interface UploadedImage {
  id: string;
  url: string;
  file: File;
  isUploading: boolean;
}

function getRelativeTime(timestamp: number): string {
  const now = Math.floor(Date.now() / 1000);
  const diff = now - timestamp;

  if (diff < 60) return 'just now';
  if (diff < 3600) {
    const mins = Math.floor(diff / 60);
    return `${mins} ${mins === 1 ? 'minute' : 'minutes'} ago`;
  }
  if (diff < 86400) {
    const hours = Math.floor(diff / 3600);
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  }
  if (diff < 604800) {
    const days = Math.floor(diff / 86400);
    return `${days} ${days === 1 ? 'day' : 'days'} ago`;
  }
  
  // For older messages, show the date
  return new Date(timestamp * 1000).toLocaleDateString();
}

// Transform API message to ChatMessage component format
function transformMessage(apiMsg: ApiMessage, currentUserId: string): Message {
  const isMyMessage = currentUserId === apiMsg.user_id;
  
  return {
    id: apiMsg.id,
    type: isMyMessage ? 'self' : 'other',
    content: apiMsg.body,
    sender: apiMsg.user_name,
    timestamp: getRelativeTime(apiMsg.created_at),
    avatarUrl: apiMsg.profile_image_url || undefined,
    imageUrl: apiMsg.image_url || apiMsg.image_urls?.[0] || undefined,
  };
}

// Fallback height for chat bar before ResizeObserver measures it
const DEFAULT_CHAT_BAR_HEIGHT = 70;

export default function ClubChatPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user: currentUser, logout } = useAuth();
  
  // React Query hooks
  const { data: clubData, isLoading: clubLoading, error: clubError } = useClub(slug || '');
  const club = clubData?.club;
  const clubId = club?.id || '';
  
  const { 
    data: messagesData, 
    isLoading: messagesLoading,
    error: messagesError,
  } = useClubMessages(clubId);
  
  const sendMessageMutation = useSendMessage(clubId);
  
  // Local UI state
  const [, forceUpdate] = useState(0);
  const [attachedImages, setAttachedImages] = useState<UploadedImage[]>([]);
  const [chatBarHeight, setChatBarHeight] = useState(DEFAULT_CHAT_BAR_HEIGHT);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const chatBarRef = useRef<HTMLDivElement>(null);
  const prevMessagesLengthRef = useRef(0);
  const hasInitializedRef = useRef(false);
  
  // Flatten all pages of messages into a single array
  const apiMessages = messagesData?.pages.flatMap(page => page.messages) || [];
  
  // Transform API messages to ChatMessage format
  const messages: Message[] = apiMessages.map(msg => 
    transformMessage(msg, currentUser?.id || '')
  );

  // Get last message for dependency optimization
  const lastMessage = messages[messages.length - 1];

  // Smart auto-scroll: jump to bottom on first load, then only scroll when user sends OR already near bottom
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const isNewMessage = messages.length > prevMessagesLengthRef.current;
    const isFromUser = lastMessage?.type === 'self';

    const isNearBottom = 
      container.scrollHeight - container.scrollTop - container.clientHeight < 100;

    // On first load, always jump to bottom
    if (!hasInitializedRef.current && messages.length > 0) {
      container.scrollTop = container.scrollHeight;
      hasInitializedRef.current = true;
    } else if (isNewMessage && (isFromUser || isNearBottom)) {
      // Auto-scroll only if:
      // 1. User sent the message (optimistic update), OR
      // 2. User is already near bottom (watching conversation)
      container.scrollTop = container.scrollHeight;
    }

    prevMessagesLengthRef.current = messages.length;
  }, [messages.length, lastMessage?.id, lastMessage?.type]);

  // Measure chat bar height dynamically (handles image attachments growing the bar)
  useEffect(() => {
    const el = chatBarRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // Use modern borderBoxSize for sub-pixel accuracy, fallback to offsetHeight
        const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.target.getBoundingClientRect().height;
        setChatBarHeight(height);
      }
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, [attachedImages.length]); // Re-observe when attachments change

  // Update relative times every minute
  useEffect(() => {
    const interval = setInterval(() => {
      forceUpdate(prev => prev + 1);
    }, 60000); // Update every 60 seconds

    return () => clearInterval(interval);
  }, []);

  const handleSendMessage = async (message: string) => {
    if ((!message.trim() && attachedImages.length === 0) || !club || sendMessageMutation.isPending) return;

    // Check if any images are still uploading
    const isUploading = attachedImages.some(img => img.isUploading);
    if (isUploading) return;

    try {
      const payload: SendMessagePayload = {
        body: message.trim() || '',
      };
      
      // Add images if available (backend expects image_urls array)
      const uploadedImageUrls = attachedImages
        .filter(img => !img.isUploading)
        .map(img => img.url);
      
      if (uploadedImageUrls.length > 0) {
        payload.image_urls = uploadedImageUrls;
      }
      
      await sendMessageMutation.mutateAsync(payload);
      
      // Clear attached images after successful send
      setAttachedImages([]);
    } catch (err) {
      console.error('Failed to send message:', err);
    }
  };

  const handleRemoveImage = (imageId: string) => {
    setAttachedImages(prev => prev.filter(img => img.id !== imageId));
  };

  if (clubLoading || messagesLoading) {
    return (
      <div className="flex items-center justify-center h-full bg-oro-bg">
        <div className="text-text-muted">Loading chat...</div>
      </div>
    );
  }

  if (clubError || messagesError || !club) {
    return (
      <div className="flex items-center justify-center h-full bg-oro-bg px-4">
        <div className="bg-oro-surface border border-oro-border rounded-lg p-4 text-text-primary">
          {clubError?.message || messagesError?.message || 'Failed to load chat'}
        </div>
      </div>
    );
  }

  // Create mobile menu configuration
  const clubChatMenu = slug ? createClubChatMobileMenu(
    navigate,
    logout,
    slug
  ) : undefined;

  return (
    <div className="flex flex-col h-full bg-oro-bg">
      {/* Sticky header - always visible, never scrolls */}
      <div className="sticky top-0 z-30 bg-oro-surface">
        <TopBar 
          title={club.name}
          onBack={() => navigate(`/clubs/${slug}`)}
          mobileMenu={clubChatMenu}
        />
      </div>
      
      {/* Scrollable messages area with padding for fixed chat input + bottom nav */}
      <div 
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto"
        style={{
          paddingBottom: chatBarHeight || 80, // Fallback to 80px if not measured yet
        }}
      >
        <ChatArea 
          messages={messages}
          onScroll={() => {}}
        />
      </div>
      
      {/* Fixed input bar - glued to top of bottom nav */}
      <ChatInputBar 
        containerRef={chatBarRef}
        onSendMessage={handleSendMessage}
        attachedImages={attachedImages}
        onAttachedImagesChange={setAttachedImages}
        onRemoveImage={handleRemoveImage}
        clubId={clubId}
        isSending={sendMessageMutation.isPending}
      />
    </div>
  );
}
